import React, { useState, useEffect } from 'react';
import { inventoryService } from '../../services/api';
import SearchableDropdown from '../common/SearchableDropdown';

interface Unit {
  id: number;
  name: string;
  symbol: string;
  parent_id?: number;
  conversion_factor: number;
  is_active: boolean;
}

interface UnitFormProps {
  unit?: Unit;
  onSave: (unitData: any) => void;
  onCancel: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  resetForm?: boolean;
}

const UnitForm: React.FC<UnitFormProps> = ({ 
  unit, 
  onSave, 
  onCancel, 
  isCollapsed, 
  onToggleCollapse, 
  resetForm
}) => {
  const [units, setUnits] = useState<Unit[]>([]);
  const [formData, setFormData] = useState({
    name: unit?.name || '',
    symbol: unit?.symbol || '',
    parent_id: unit?.parent_id || '',
    conversion_factor: unit?.conversion_factor || 1.0,
    is_active: unit?.is_active ?? true
  });
  
  useEffect(() => {
    loadUnits();
  }, []);

  useEffect(() => {
    if (resetForm && !unit) {
      setFormData({
        name: '',
        symbol: '',
        parent_id: '',
        conversion_factor: 1.0,
        is_active: true
      });
    } else if (unit) {
      setFormData({
        name: unit.name,
        symbol: unit.symbol,
        parent_id: unit.parent_id || '',
        conversion_factor: unit.conversion_factor || 1.0,
        is_active: unit.is_active
      });
    }
  }, [unit, resetForm]);

  const loadUnits = async () => {
    try {
      const response = await inventoryService.getUnits({ page: 1, per_page: 100 });
      setUnits(response.data);
    } catch (error) {
      console.error('Failed to load units:', error);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      parent_id: formData.parent_id === '' ? null : formData.parent_id,
      conversion_factor: formData.conversion_factor || 1.0
    };
    onSave(submitData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              name === 'conversion_factor' ? parseFloat(value) || 0 :
              name === 'parent_id' ? (value === '' ? '' : parseInt(value)) :
              value
    }));
  };

  const handleParentUnitChange = (value: string | number | (string | number)[]) => {
    const parentId = Array.isArray(value) ? value[0] : value;
    setFormData(prev => ({
      ...prev,
      parent_id: parentId
    }));
  };

  return (
    <>
      {!isCollapsed && (
        <form onSubmit={handleSubmit} className="erp-form-body">
          <div className="grid grid-cols-1 sm:grid-cols-6 erp-form-grid">
            <div>
              <label className="erp-form-label">Unit Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="erp-form-input"
              />
            </div>

            <div>
              <label className="erp-form-label">Symbol</label>
              <input
                type="text"
                name="symbol"
                value={formData.symbol}
                onChange={handleChange}
                className="erp-form-input"
              />
            </div>

            <div>
              <label className="erp-form-label">Parent Unit</label>
              <SearchableDropdown
                options={units.filter(u => u.id !== unit?.id).map(u => ({
                  value: u.id,
                  label: u.name
                }))}
                value={formData.parent_id}
                onChange={handleParentUnitChange}
                placeholder="Select parent unit..."
                multiple={false}
                searchable={true}
                className="w-full"
              />
            </div>

            <div>
              <label className="erp-form-label">Conversion Factor</label>
              <input
                type="number"
                step="0.0001"
                name="conversion_factor"
                value={formData.conversion_factor}
                onChange={handleChange}
                className="erp-form-input"
              />
            </div>

            <div>
              <label className="erp-form-label">Status</label>
              <select
                name="is_active"
                value={formData.is_active ? 'true' : 'false'}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                className="erp-form-input"
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end" style={{ gap: 'var(--erp-spacing-sm)' }}>
            <button
              type="button"
              onClick={onCancel}
              className="erp-form-btn text-gray-700 bg-gray-100 hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="erp-form-btn text-white bg-primary hover:bg-secondary"
            >
              {unit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      )}
    </>
  );
};

export default UnitForm;